import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: string;
  sortable?: boolean;
  render: (item: T, index: number) => React.ReactNode;
}

export interface SortConfig {
  field: string;
  direction: 'asc' | 'desc';
}

export interface DragDropTableProps<T> {
  items: T[];
  columns: TableColumn<T>[];
  keyExtractor: (item: T) => string | number;
  onReorder: (reorderedItems: T[]) => void;
  isLoading?: boolean;
  showDragHandle?: boolean;
  renderActions?: (item: T, index: number) => React.ReactNode;
  sortConfig?: SortConfig;
  onSort?: (field: string) => void;
  // Bulk selection props
  enableBulkSelection?: boolean;
  selectedIds?: Set<string | number>;
  onSelectionChange?: (item: T) => void;
  onSelectAll?: () => void;
  onClearSelection?: () => void;
  isAllSelected?: boolean;
  isPartiallySelected?: boolean;
}

export function DragDropTable<T>({
  items,
  columns,
  keyExtractor,
  onReorder,
  isLoading = false,
  showDragHandle = true,
  renderActions,
  sortConfig,
  onSort,
  enableBulkSelection = false,
  selectedIds,
  onSelectionChange,
  onSelectAll,
  onClearSelection,
  isAllSelected = false,
  isPartiallySelected = false,
}: DragDropTableProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex((item) => keyExtractor(item) === active.id);
      const newIndex = items.findIndex((item) => keyExtractor(item) === over?.id);

      const reorderedItems = arrayMove(items, oldIndex, newIndex);
      onReorder(reorderedItems);
    }
  };

  const handleSort = (field: string) => {
    if (onSort) {
      onSort(field);
    }
  };

  const getSortIcon = (field: string) => {
    if (!sortConfig || sortConfig.field !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
        </svg>
      );
    }

    return (
      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
      </svg>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              {enableBulkSelection && (
                <th className="text-left py-3 px-2 font-medium text-gray-700 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = isPartiallySelected;
                      }
                    }}
                    onChange={isAllSelected ? onClearSelection : onSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? "Deselect all" : "Select all"}
                  />
                </th>
              )}
              {showDragHandle && (
                <th className="text-left py-3 px-2 font-medium text-gray-700 w-8"></th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`text-left py-2 px-3 font-medium text-gray-700 text-sm ${column.width || ''}`}
                >
                  {column.sortable && onSort ? (
                    <button
                      onClick={() => handleSort(column.key)}
                      className="flex items-center space-x-1 hover:text-gray-900 focus:outline-none focus:text-gray-900"
                    >
                      <span>{column.label}</span>
                      {getSortIcon(column.key)}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              ))}
              {renderActions && (
                <th className="text-right py-2 px-3 font-medium text-gray-700 text-sm">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            <SortableContext
              items={items.map((item) => keyExtractor(item))}
              strategy={verticalListSortingStrategy}
            >
              {items.map((item, index) => (
                <DragDropTableRow
                  key={keyExtractor(item)}
                  id={keyExtractor(item)}
                  item={item}
                  index={index}
                  columns={columns}
                  showDragHandle={showDragHandle}
                  renderActions={renderActions}
                  disabled={isLoading}
                  enableBulkSelection={enableBulkSelection}
                  isSelected={selectedIds?.has(keyExtractor(item))}
                  onSelectionChange={onSelectionChange}
                />
              ))}
            </SortableContext>
          </tbody>
        </table>
      </div>
    </DndContext>
  );
}

interface DragDropTableRowProps<T> {
  id: string | number;
  item: T;
  index: number;
  columns: TableColumn<T>[];
  showDragHandle: boolean;
  renderActions?: (item: T, index: number) => React.ReactNode;
  disabled?: boolean;
  // Bulk selection props
  enableBulkSelection?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (item: T) => void;
}

function DragDropTableRow<T>({
  id,
  item,
  index,
  columns,
  showDragHandle,
  renderActions,
  disabled = false,
  enableBulkSelection = false,
  isSelected = false,
  onSelectionChange,
}: DragDropTableRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b hover:bg-gray-50 ${
        isDragging ? 'opacity-50 z-50 bg-white shadow-lg' : ''
      } ${disabled ? 'opacity-60' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
      data-equipment-row
      tabIndex={0}
    >
      {enableBulkSelection && (
        <td className="py-2 px-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onSelectionChange?.(item)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            disabled={disabled}
          />
        </td>
      )}
      {showDragHandle && (
        <td className="py-2 px-2">
          <div
            {...attributes}
            {...listeners}
            className={`cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 ${
              disabled ? 'cursor-not-allowed opacity-50' : ''
            }`}
            title={disabled ? 'Reordering disabled during operations' : 'Drag to reorder'}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              className="w-4 h-4"
            >
              <circle cx="3" cy="3" r="1.5" />
              <circle cx="3" cy="8" r="1.5" />
              <circle cx="3" cy="13" r="1.5" />
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
              <circle cx="13" cy="3" r="1.5" />
              <circle cx="13" cy="8" r="1.5" />
              <circle cx="13" cy="13" r="1.5" />
            </svg>
          </div>
        </td>
      )}
      {columns.map((column) => (
        <td key={column.key} className="py-2 px-3">
          {column.render(item, index)}
        </td>
      ))}
      {renderActions && (
        <td className="py-2 px-3">
          <div className="flex justify-end space-x-1">
            {renderActions(item, index)}
          </div>
        </td>
      )}
    </tr>
  );
}